import React, { Fragment, useEffect, useState } from 'react'
import { StandardPage } from '../../components/_EOS/StandardPage'
import { Filter, FilterComponent } from '../../components/_WMS/FilterComponent'
import { FilterExtraAction } from '../../components/_WMS/FilterComponent/types'
import { filterOptions } from './config/filterOptions'
import { usePagination } from '../../hooks/usePagination'
import { GenericOrderBy } from '../../components/_EOS/Table'
import { getSkuList } from './config/api'
import { useApi } from '../../hooks/useApi'
import { ApiResponse } from '../../typings/ApiResponse'
import { ProductAdmin } from '../../interfaces/Producto/ProductAdmin'
import { useSnackbar } from '../../hooks/useSnackbar'
import { GenericTableV2 } from '../../components/_EOS/GenericTable/GenericTableV2'
import { Status } from '../../hooks/useFetchState'
import { getColumnsConfig } from './config/tableColumns'
import { SnackBar } from '../../components/SnackBar/Snackbar'
import { useModal } from '../../hooks/useModal'
import { Settings } from '@mui/icons-material'
import AssignmentIcon from '@mui/icons-material/Assignment'
import { Modal } from '../../components/_EOS/Modal'
import { ConfigProductModal } from './modal/ConfigProductModal'
import { ConfigMode } from './types'
import { VasRuleSkuModal } from './modal/VasRuleSkuModal'
import { VasRuleMasiveModal } from './modal/VasRuleMasiveModal'
import QueueIcon from '@mui/icons-material/Queue'
import PostAddIcon from '@mui/icons-material/PostAdd'

import { ExternalStorageMasiveModal } from './modal/ExternalStorageMasiveModal'

interface ProductAdminPageProps {}

export const ProductAdminPage: React.FC<ProductAdminPageProps> = () => {
  const { callApi, state } = useApi<ApiResponse<ProductAdmin>>()
  const { isActive, message, openSnackBar, isSuccess } = useSnackbar()
  const [mode, setMode] = useState<ConfigMode>('MENU')
  // Filtros que el usuario edita
  const [draftFilters, setDraftFilters] = useState<Filter[]>([])

  // Filtros realmente aplicados a la búsqueda
  const [appliedFilters, setAppliedFilters] = useState<Filter[]>([])

  const pagination = usePagination(5)

  const [orderBy, setOrderBy] = useState<GenericOrderBy<ProductAdmin>>({
    field: 'id' as keyof ProductAdmin,
    direction: 'asc'
  })

  const columnsConfig = getColumnsConfig()

  const handleSorting = (
    field: keyof ProductAdmin,
    direction: 'asc' | 'desc'
  ) => {
    setOrderBy({ field, direction })
  }

  const handlePagination = (page: number, pageSize: number) => {
    const auxPage = pagination.pageSize !== pageSize ? 0 : page
    pagination.setPagination(auxPage, pageSize)
  }

  const requestData = (filters: Filter[], page: number, pageSize: number) => {
    callApi(getSkuList(filters, page, pageSize, orderBy)).catch((error) => {
      openSnackBar(error, false)
    })
  }

  useEffect(() => {
    requestData(appliedFilters, pagination.page, pagination.pageSize)
  }, [pagination.page, pagination.pageSize, appliedFilters, orderBy])

  const configModal = useModal<{ Product: ProductAdmin }>('Configurar Producto')
  const viewVasModal = useModal<{ Product: ProductAdmin }>('Detalle de Vas')
  const vasRuleMasiveModal = useModal('Asociación Masiva de VAS')
  const externalStorageMasiveModal = useModal('Asociación Masiva')

  const getModalSize = (mode: ConfigMode) => {
    switch (mode) {
      case 'VAS':
        return 'md'
      case 'STORAGE':
        return 'sm'
      default:
        return 'sm'
    }
  }

  const getModalTitle = (mode: ConfigMode) => {
    switch (mode) {
      case 'VAS':
        return 'Configurar Regla VAS'
      case 'STORAGE':
        return 'Almacenamiento Externo'
      default:
        return 'Configurar Producto'
    }
  }

  const extraActions: FilterExtraAction[] = [
    {
      label: 'Asociación Masiva de VAS',
      onClick: () => vasRuleMasiveModal.openModal('sm'),
      icon: <QueueIcon />
    },
    {
      label: 'Asociación Masiva de Almacenamiento Externo',
      onClick: () => externalStorageMasiveModal.openModal('sm'),
      icon: <PostAddIcon />
    }
  ]

  return (
    <Fragment>
      <StandardPage pageTitle=''>
        <FilterComponent
          includeAddButton={true}
          options={filterOptions}
          onSearchClick={(filters?: Filter[]) => {
            pagination.setPagination(0, pagination.pageSize)
            setAppliedFilters(filters || draftFilters)
          }}
          selectedFilters={draftFilters}
          setSelectedFilters={setDraftFilters}
          includeSearchOnClean={true}
          extraActions={extraActions}
        />
        <GenericTableV2<ProductAdmin>
          data={state.data?.items || []}
          columns={columnsConfig}
          keyField='id'
          loading={state.status === Status.Loading}
          pagination={{
            page: pagination.page,
            pageSize: pagination.pageSize,
            total: state.data?.totalItems || 0,
            onPageChange: handlePagination
          }}
          sorting={{
            field: orderBy.field as keyof ProductAdmin,
            direction: orderBy.direction,
            onSortChange: handleSorting
          }}
          actions={[
            {
              key: 'config',
              label: 'Configuración',
              onClick: (item) => configModal.openModal('sm', { Product: item }),
              icon: <Settings />
            },
            {
              key: 'vasRule',
              label: 'Reglas VAS',
              disabled: (item) => !item.withVas,
              onClick: (item) =>
                viewVasModal.openModal('md', { Product: item }),
              icon: <AssignmentIcon />
            }
          ]}
        />
      </StandardPage>
      <Modal
        open={configModal.isOpen}
        maxWidth={getModalSize(mode)}
        title={getModalTitle(mode)}
        onClose={() => {
          setMode('MENU')
          configModal.closeModal()
        }}
        loading={state.status === Status.Loading}
      >
        <ConfigProductModal
          mode={mode}
          setMode={setMode}
          handleSnackbar={openSnackBar}
          skuId={configModal.extendedProps?.Product.id || 0}
          sku={configModal.extendedProps?.Product.sku || ''}
          externalStorage={
            configModal.extendedProps?.Product.externalStorage ?? false
          }
          typeStorageId={configModal.extendedProps?.Product.typeStorageId ?? 0}
          onClose={() => {
            setMode('MENU')
            configModal.closeModal()
          }}
          requestProduct={() => {
            requestData(appliedFilters, pagination.page, pagination.pageSize)
          }}
        />
      </Modal>
      <Modal
        open={viewVasModal.isOpen}
        maxWidth={viewVasModal.size}
        title={viewVasModal.title}
        onClose={() => {
          viewVasModal.closeModal()
        }}
        loading={state.status === Status.Loading}
      >
        <VasRuleSkuModal
          handleSnackbar={openSnackBar}
          skuId={viewVasModal.extendedProps?.Product.id || 0}
        />
      </Modal>
      <Modal
        open={vasRuleMasiveModal.isOpen}
        maxWidth={vasRuleMasiveModal.size}
        title={vasRuleMasiveModal.title}
        onClose={() => {
          vasRuleMasiveModal.closeModal()
        }}
      >
        <VasRuleMasiveModal
          handleSnackbar={openSnackBar}
          handleCloseModal={vasRuleMasiveModal.closeModal}
        />
      </Modal>

      <Modal
        open={externalStorageMasiveModal.isOpen}
        maxWidth={externalStorageMasiveModal.size}
        title={externalStorageMasiveModal.title}
        onClose={() => {
          externalStorageMasiveModal.closeModal()
        }}
      >
        <ExternalStorageMasiveModal
          handleSnackbar={openSnackBar}
          handleCloseModal={externalStorageMasiveModal.closeModal}
        />
      </Modal>

      <SnackBar
        message={message}
        status={isSuccess ? 'success' : 'error'}
        open={isActive}
      />
    </Fragment>
  )
}
